import type { LevelCatalogEntry, RunSubmission } from "./types";
import { effectiveTargetLengthFor, isPlatform } from "../platform/Platform";

/**
 * Shared validation rules for player-supplied data (display names, run
 * submissions). Pure and framework-agnostic on purpose: both the browser
 * (for instant client-side feedback) and the Netlify Functions layer (as
 * the actual security boundary - the client-side check is only a UX
 * nicety, never trusted) import these same functions, so the two can
 * never silently drift apart.
 */

const NAME_MIN = 2;
const NAME_MAX = 16;
/** Unicode letters/numbers, a single space, `-` and `_`. Requires the `u` flag for `\p{L}`/`\p{N}` to mean "any Unicode letter/number", not just ASCII. */
const NAME_ALLOWED = /^[\p{L}\p{N}_ -]+$/u;
/** Control characters (including newlines/tabs) and angle brackets (blocks HTML) - checked against the raw, pre-trim input so a name that's *only* a newline can't slip through as an empty-after-trim string either. */
const NAME_FORBIDDEN = /[\p{Cc}<>]/u;

export interface NameValidationResult {
  valid: boolean;
  /** Trimmed, whitespace-collapsed form - what actually gets stored/displayed when `valid`. */
  normalized: string;
  error?: string;
}

/** 2-16 chars after trim/whitespace-normalization, Unicode letters/numbers/single-space/-/_ only, no HTML, no newlines. */
export function validateDisplayName(raw: unknown): NameValidationResult {
  if (typeof raw !== "string") {
    return { valid: false, normalized: "", error: "Name is required." };
  }
  if (NAME_FORBIDDEN.test(raw)) {
    return { valid: false, normalized: raw.trim(), error: "Name can't contain HTML or control characters." };
  }
  const normalized = raw.trim().replace(/ {2,}/g, " ");
  if (normalized.length < NAME_MIN || normalized.length > NAME_MAX) {
    return { valid: false, normalized, error: `Name must be ${NAME_MIN}-${NAME_MAX} characters.` };
  }
  if (!NAME_ALLOWED.test(normalized)) {
    return { valid: false, normalized, error: "Use letters, numbers, spaces, - or _ only." };
  }
  return { valid: true, normalized };
}

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

const RUN_SOURCES = new Set<RunSubmission["runSource"]>(["play", "replay", "next_level", "level_select"]);
const DEATH_REASONS = new Set<NonNullable<RunSubmission["deathReason"]>>(["wall", "obstacle", "self"]);

/** Generous sanity ceilings, not real game limits - just enough to reject an obviously-forged payload (e.g. score: 999999999) without hardcoding per-level balance here. */
const MAX_SCORE = 100_000;
/** Score has no lower bound at 0: SCORE HIT and SHRINK (CHAOS outcomes) can legitimately leave a *completed* level with a negative score, and that must be recorded exactly - never clamped to 0, never rejected as "invalid". This ceiling only guards against an absurd/forged magnitude. */
const MIN_SCORE = -100_000;
const MAX_LENGTH = 2_000;
const MAX_DURATION_MS = 60 * 60 * 1000;
const MAX_COUNTER = 100_000;

function isNonNegInt(value: unknown, max: number): value is number {
  return typeof value === "number" && Number.isInteger(value) && value >= 0 && value <= max;
}

function isBoundedInt(value: unknown, min: number, max: number): value is number {
  return typeof value === "number" && Number.isInteger(value) && value >= min && value <= max;
}

export interface RunValidationResult {
  valid: boolean;
  errors: string[];
}

/**
 * Validates an untrusted run-submission payload against the counter
 * bounds, enum values, and (given the level's catalog row) whether a
 * completed run's `finalLength` actually reached that level's
 * `targetLength`. Does not check `run_id` uniqueness - that's an
 * idempotency concern handled at the database layer (`run_id` is the
 * primary key), not a validity concern. `score` may be negative (SCORE
 * HIT/SHRINK) - see `MIN_SCORE` above; every other numeric field stays
 * non-negative.
 */
export function validateRunSubmission(payload: unknown, level: LevelCatalogEntry | undefined): RunValidationResult {
  const errors: string[] = [];
  if (!payload || typeof payload !== "object") {
    return { valid: false, errors: ["Invalid payload."] };
  }
  const p = payload as Record<string, unknown>;

  if (typeof p.runId !== "string" || !UUID_RE.test(p.runId)) errors.push("Invalid run id.");
  if (typeof p.levelId !== "string" || !level || !level.active) errors.push("Invalid or inactive level.");
  // PR34: optional only so a pre-PR34 queued payload (no platform at all,
  // stored as "unknown") stays valid; when present it must be one of the
  // two real categories - a client can never claim "unknown" itself.
  if (p.platform !== undefined && !isPlatform(p.platform)) errors.push("Invalid platform.");
  if (typeof p.worldId !== "string" || (level && p.worldId !== level.worldId)) errors.push("World id does not match level.");
  if (p.status !== "completed" && p.status !== "failed") errors.push("Invalid status.");
  if (!isBoundedInt(p.score, MIN_SCORE, MAX_SCORE)) errors.push("Invalid score.");
  if (p.stars !== 0 && p.stars !== 1 && p.stars !== 2 && p.stars !== 3) errors.push("Invalid stars.");
  if (!isNonNegInt(p.finalLength, MAX_LENGTH)) errors.push("Invalid final length.");
  if (!isNonNegInt(p.activeDurationMs, MAX_DURATION_MS)) errors.push("Invalid active duration.");

  const counterKeys = [
    "safeCount",
    "rushCount",
    "chaosCount",
    "maxRushStreak",
    "jackpotCount",
    "speedTrialStartedCount",
    "speedTrialSurvivedCount",
    "scoreHitCount",
    "shrinkCount",
  ] as const;
  for (const key of counterKeys) {
    if (!isNonNegInt(p[key], MAX_COUNTER)) errors.push(`Invalid ${key}.`);
  }
  if (
    typeof p.speedTrialSurvivedCount === "number" &&
    typeof p.speedTrialStartedCount === "number" &&
    p.speedTrialSurvivedCount > p.speedTrialStartedCount
  ) {
    errors.push("speedTrialSurvivedCount cannot exceed speedTrialStartedCount.");
  }

  // Backward compatibility: an old (pre-WORMHOLE/STARFRUIT) client never
  // sends these 4 fields at all - default them exactly as an old,
  // length-target-only run always implicitly meant, rather than reject an
  // otherwise-valid legacy payload. A payload that *does* send them is
  // validated against its own explicit values, not the defaults.
  const wormholeCount = p.wormholeCount === undefined ? 0 : p.wormholeCount;
  const starfruitSpawnedCount = p.starfruitSpawnedCount === undefined ? 0 : p.starfruitSpawnedCount;
  const starfruitEatenCount = p.starfruitEatenCount === undefined ? 0 : p.starfruitEatenCount;
  const completionReason =
    p.completionReason === undefined ? (p.status === "completed" ? "length_target" : null) : p.completionReason;

  if (!isNonNegInt(wormholeCount, MAX_COUNTER)) errors.push("Invalid wormholeCount.");
  if (starfruitSpawnedCount !== 0 && starfruitSpawnedCount !== 1) errors.push("Invalid starfruitSpawnedCount.");
  if (starfruitEatenCount !== 0 && starfruitEatenCount !== 1) errors.push("Invalid starfruitEatenCount.");
  if (
    typeof starfruitEatenCount === "number" &&
    typeof starfruitSpawnedCount === "number" &&
    starfruitEatenCount > starfruitSpawnedCount
  ) {
    errors.push("starfruitEatenCount cannot exceed starfruitSpawnedCount.");
  }

  if (p.status === "completed") {
    if (p.deathReason !== null) errors.push("A completed run cannot have a death reason.");
    if (completionReason !== "length_target" && completionReason !== "starfruit_gate") {
      errors.push("Invalid completion reason.");
    } else if (completionReason === "length_target") {
      // PR34: the EFFECTIVE target for this run's platform (Mobile
      // Mars/Saturn/Sun: Math.round(raw * 0.85); otherwise the raw catalog
      // target) - the same rule `submit_run()` enforces in SQL.
      const platform = isPlatform(p.platform) ? p.platform : undefined;
      if (level && isNonNegInt(p.finalLength, MAX_LENGTH) && p.finalLength < effectiveTargetLengthFor(level.worldId, level.targetLength, platform)) {
        errors.push("Completed run did not reach the level's target length.");
      }
    } else if (completionReason === "starfruit_gate" && starfruitEatenCount !== 1) {
      errors.push("A starfruit_gate completion requires starfruit to have been eaten.");
    }
  } else {
    if (completionReason !== null) errors.push("A failed run cannot have a completion reason.");
    if (typeof p.deathReason !== "string" || !DEATH_REASONS.has(p.deathReason as never)) {
      errors.push("Invalid death reason.");
    }
  }

  if (typeof p.runSource !== "string" || !RUN_SOURCES.has(p.runSource as never)) errors.push("Invalid run source.");

  validateScoringV2Fields(p, errors, completionReason, starfruitEatenCount);

  return { valid: errors.length === 0, errors };
}

/**
 * Scoring V2 fields, all optional on the wire for backward compatibility -
 * an old (pre-Scoring-V2) client never sends any of them at all. Missing
 * entirely defaults to `scoringVersion: 1` (Bite Score only, matching
 * exactly what that client always meant): `biteScore = score`,
 * `skillScore`/`paceBonus`/every counter = 0. A payload that *does* send
 * scoring V2 fields is validated against its own explicit values, not the
 * defaults - same precedent as WORMHOLE/STARFRUIT's own backward-compat
 * handling above.
 */
function validateScoringV2Fields(p: Record<string, unknown>, errors: string[], completionReason: unknown, starfruitEatenCount: unknown): void {
  const scoringVersion = p.scoringVersion === undefined ? 1 : p.scoringVersion;
  if (scoringVersion !== 1 && scoringVersion !== 2) {
    errors.push("Invalid scoringVersion.");
    return;
  }

  const score = typeof p.score === "number" ? p.score : 0;
  const rawBiteScore = p.biteScore === undefined ? score : p.biteScore;
  const rawSkillScore = p.skillScore === undefined ? 0 : p.skillScore;
  const rawPaceBonus = p.paceBonus === undefined ? 0 : p.paceBonus;
  const rawMoveCount = p.moveCount === undefined ? 0 : p.moveCount;

  if (!isBoundedInt(rawBiteScore, MIN_SCORE, MAX_SCORE)) errors.push("Invalid biteScore.");
  if (!isNonNegInt(rawSkillScore, MAX_SCORE)) errors.push("Invalid skillScore.");
  if (!isNonNegInt(rawPaceBonus, MAX_SCORE)) errors.push("Invalid paceBonus.");
  if (!isNonNegInt(rawMoveCount, MAX_COUNTER)) errors.push("Invalid moveCount.");

  const maneuverKeys = ["perfectFoldCount", "lastSecondTurnCount", "tailGrazeCount", "threadTheNeedleCount", "doubleDangerCount", "bestFlowStreak"] as const;
  const maneuverValues: Record<string, number> = {};
  for (const key of maneuverKeys) {
    const value = p[key] === undefined ? 0 : p[key];
    if (!isNonNegInt(value, MAX_COUNTER)) {
      errors.push(`Invalid ${key}.`);
    } else {
      maneuverValues[key] = value;
    }
  }

  const pauseCount = p.pauseCount === undefined ? 0 : p.pauseCount;
  const pausedDurationMs = p.pausedDurationMs === undefined ? 0 : p.pausedDurationMs;
  if (!isNonNegInt(pauseCount, MAX_COUNTER)) errors.push("Invalid pauseCount.");
  if (!isNonNegInt(pausedDurationMs, MAX_DURATION_MS)) errors.push("Invalid pausedDurationMs.");

  // TRICK BITE / CLUTCH BITE: both optional on the wire (an old client
  // never sends either), same backward-compat precedent as every other
  // Scoring V2 counter above.
  const rawTrickBiteCount = p.trickBiteCount === undefined ? 0 : p.trickBiteCount;
  const rawClutchBiteCount = p.clutchBiteCount === undefined ? 0 : p.clutchBiteCount;
  if (!isNonNegInt(rawTrickBiteCount, MAX_COUNTER)) errors.push("Invalid trickBiteCount.");
  if (!isNonNegInt(rawClutchBiteCount, MAX_COUNTER)) errors.push("Invalid clutchBiteCount.");

  // Only meaningful once every individual field above is already known-valid
  // - safe to treat every raw* value as a real `number` from here on.
  if (errors.length > 0) return;
  const biteScore = rawBiteScore as number;
  const skillScore = rawSkillScore as number;
  const paceBonus = rawPaceBonus as number;
  const moveCount = rawMoveCount as number;
  const trickBiteCount = rawTrickBiteCount as number;
  const clutchBiteCount = rawClutchBiteCount as number;

  // score = biteScore + skillScore + paceBonus, always - the single most
  // important server-side consistency check for Scoring V2.
  if (typeof p.score === "number" && score !== biteScore + skillScore + paceBonus) {
    errors.push("score must equal biteScore + skillScore + paceBonus.");
  }

  // A failed run, or a starfruit_gate completion, must always carry 0 Pace Bonus.
  if ((p.status === "failed" || completionReason === "starfruit_gate") && paceBonus !== 0) {
    errors.push("paceBonus must be 0 for a failed run or a starfruit_gate completion.");
  }

  // The server can't reconstruct every maneuver from movement history, but
  // it can reject a payload whose counters are implausible relative to
  // its own claimed moveCount - a sanity ceiling, not a replay.
  const totalManeuvers =
    (maneuverValues.perfectFoldCount ?? 0) +
    (maneuverValues.lastSecondTurnCount ?? 0) +
    (maneuverValues.tailGrazeCount ?? 0) +
    (maneuverValues.threadTheNeedleCount ?? 0);
  if (totalManeuvers > moveCount) errors.push("Maneuver counts exceed moveCount.");
  if ((maneuverValues.bestFlowStreak ?? 0) > moveCount) errors.push("bestFlowStreak exceeds moveCount.");
  if ((maneuverValues.doubleDangerCount ?? 0) > Math.min(totalManeuvers, moveCount)) errors.push("doubleDangerCount exceeds plausible bounds.");

  // TRICK BITE + CLUTCH BITE can never exceed the total number of fruits
  // actually eaten this run (safeCount/rushCount/chaosCount/
  // starfruitEatenCount are all already known-valid non-negative ints by
  // this point - validated earlier in validateRunSubmission, into the same
  // shared `errors` array this function's own "return if errors.length > 0"
  // gate above already covers).
  const safeCount = p.safeCount as number;
  const rushCount = p.rushCount as number;
  const chaosCount = p.chaosCount as number;
  const totalFruitsEaten = safeCount + rushCount + chaosCount + (starfruitEatenCount as number);
  if (trickBiteCount + clutchBiteCount > totalFruitsEaten) {
    errors.push("trickBiteCount + clutchBiteCount cannot exceed the total number of fruits eaten.");
  }
}
